/**
 * BLRRD Selection — ellipse.js  (Phase 4.B.select.1)
 *
 * Rasterize an axis-aligned ellipse into a Float32 coverage mask.
 *
 * Algorithm — 4× supersampling + implicit equation
 * ───────────────────────────────────────────────
 * For each pixel, sample 4 sub-pixel positions on a 2×2 grid and count
 * how many fall inside the implicit form
 *
 *     ((sx - cx)/rx)² + ((sy - cy)/ry)² ≤ 1
 *
 * Coverage = inside_count / 4. The 4-tap pattern keeps the cost low
 * while delivering Wu-style edge AA quality for circular / ellipsoidal
 * shapes. Citations: Wu (1991), "An efficient antialiasing technique";
 * the technique generalises trivially to ellipses via the implicit form.
 *
 * The outer bounding box is the same as `rect.js` — we never touch
 * pixels outside the ellipse's AABB, so cost scales with the visible
 * area, not the canvas size.
 *
 * Provenance: clean-room. Implicit-equation rasterisation + 4× SS is
 * universal — every introductory graphics text covers it.
 */

/**
 * Rasterize an ellipse defined by centre (cx, cy) and radii (rx, ry).
 *
 *   ((x - cx)/rx)² + ((y - cy)/ry)² ≤ 1
 *
 * Mutates `mask` in place. Caller is responsible for combine semantics.
 *
 * @param {Float32Array} mask
 * @param {number} w
 * @param {number} h
 * @param {{cx: number, cy: number, rx: number, ry: number}} ellipse
 * @param {boolean} [antiAlias=true]    when false, single-sample hard edge
 * @returns {Float32Array}              the same `mask` reference
 */
export function rasterizeEllipse(mask, w, h, ellipse, antiAlias = true) {
    if (!(mask instanceof Float32Array)) {
        throw new Error('rasterizeEllipse: mask must be Float32Array');
    }
    const W = w | 0, H = h | 0;
    if (mask.length !== W * H) {
        throw new Error(`rasterizeEllipse: mask length mismatch (${mask.length} vs ${W * H})`);
    }
    if (!ellipse) return mask;

    const { cx, cy } = ellipse;
    let rx = ellipse.rx, ry = ellipse.ry;
    if (!Number.isFinite(cx) || !Number.isFinite(cy)
            || !Number.isFinite(rx) || !Number.isFinite(ry)) {
        return mask;
    }
    /* Negative or zero radii — degenerate, leave mask zeroed. */
    if (rx <= 0 || ry <= 0) { mask.fill(0); return mask; }

    mask.fill(0);

    /* AABB. Floor / ceil to include pixels partially covered by the
     * ellipse boundary. */
    const xLo = Math.max(0, Math.floor(cx - rx));
    const xHi = Math.min(W, Math.ceil(cx + rx) + 1);
    const yLo = Math.max(0, Math.floor(cy - ry));
    const yHi = Math.min(H, Math.ceil(cy + ry) + 1);

    /* Precompute reciprocals once. 1 / (r * r) saves a division per
     * sample inside the inner loop. */
    const inv_rx2 = 1 / (rx * rx);
    const inv_ry2 = 1 / (ry * ry);

    /* 2x2 supersample offsets (0.25, 0.75 within the pixel). */
    const OFFS = antiAlias ? [0.25, 0.75] : [0.5];
    const SAMPLES = OFFS.length * OFFS.length;   /* 4 or 1 */
    const INV_SAMPLES = 1 / SAMPLES;

    for (let y = yLo; y < yHi; y++) {
        const rowBase = y * W;
        for (let x = xLo; x < xHi; x++) {
            let inside = 0;
            for (let dy = 0; dy < OFFS.length; dy++) {
                const sy = y + OFFS[dy];
                const ny = (sy - cy);
                const ny2 = ny * ny * inv_ry2;
                if (ny2 > 1) continue;     /* whole sub-row outside */
                for (let dx = 0; dx < OFFS.length; dx++) {
                    const sx = x + OFFS[dx];
                    const nx = (sx - cx);
                    if (nx * nx * inv_rx2 + ny2 <= 1) inside++;
                }
            }
            if (inside > 0) {
                const cov = inside * INV_SAMPLES;
                mask[rowBase + x] = cov;
            }
        }
    }
    return mask;
}
